import { create } from 'zustand';
import type { Participant, Room } from '@ember/types';
import type { RoomWebSocket } from '../lib/room-ws.js';

export interface ChatMessage {
  id: string; // client-generated UUID for React key
  senderId: string;
  senderName: string;
  text: string;
  timestamp: string;
}

interface RoomState {
  // Room metadata
  room: Room | null;
  slug: string;

  // Persistent WebSocket (lives across LobbyPage → RoomPage navigation)
  // Stored outside React so it isn't affected by component unmounts.
  ws: RoomWebSocket | null;
  setWs: (ws: RoomWebSocket | null) => void;

  // Participant state
  participants: Participant[];
  lobbyQueue: Participant[]; // participants waiting in lobby (admin view)

  // Chat
  chatMessages: ChatMessage[];

  // Pre-join device preferences (set on pre-join screen, read by RoomPage)
  preJoinPreferences: { audio: boolean; video: boolean };

  // Connection state
  isAdmitted: boolean;
  isDenied: boolean;
  isKicked: boolean;
  roomExpired: boolean;
  expiryWarning: boolean;
  realtimekitToken: string | null;

  // Actions
  setRoom: (room: Room, slug: string) => void;
  setPreJoinPreferences: (prefs: { audio: boolean; video: boolean }) => void;
  setAdmitted: (token: string) => void;
  setDenied: () => void;
  setKicked: () => void;
  setRoomExpired: () => void;
  setExpiryWarning: (value: boolean) => void;

  addParticipant: (participant: Participant) => void;
  removeParticipant: (participantId: string) => void;
  markParticipantKicked: (participantId: string) => void;

  setLobbyQueue: (participants: Participant[]) => void;

  addChatMessage: (msg: Omit<ChatMessage, 'id'>) => void;

  resetRoom: () => void;
}

const initialState = {
  room: null,
  slug: '',
  ws: null as RoomWebSocket | null,
  participants: [],
  lobbyQueue: [],
  chatMessages: [],
  preJoinPreferences: { audio: false, video: false },
  isAdmitted: false,
  isDenied: false,
  isKicked: false,
  roomExpired: false,
  expiryWarning: false,
  realtimekitToken: null,
};

export const useRoomStore = create<RoomState>()((set) => ({
  ...initialState,

  setWs: (ws) => set({ ws }),

  setRoom: (room, slug) => set({ room, slug }),

  setPreJoinPreferences: (prefs) => set({ preJoinPreferences: prefs }),

  setAdmitted: (token) => set({ isAdmitted: true, realtimekitToken: token }),

  setDenied: () => set({ isDenied: true }),

  setKicked: () => set({ isKicked: true }),

  setRoomExpired: () => set({ roomExpired: true }),

  setExpiryWarning: (value) => set({ expiryWarning: value }),

  addParticipant: (participant) =>
    set((state) => ({
      participants: [
        ...state.participants.filter((p) => p.participantId !== participant.participantId),
        participant,
      ],
    })),

  removeParticipant: (participantId) =>
    set((state) => ({
      participants: state.participants.map((p) =>
        p.participantId === participantId ? { ...p, status: 'left' as const } : p,
      ),
    })),

  markParticipantKicked: (participantId) =>
    set((state) => ({
      participants: state.participants.map((p) =>
        p.participantId === participantId ? { ...p, status: 'kicked' as const } : p,
      ),
    })),

  setLobbyQueue: (participants) => set({ lobbyQueue: participants }),

  addChatMessage: (msg) =>
    set((state) => ({
      chatMessages: [
        ...state.chatMessages,
        { ...msg, id: crypto.randomUUID() },
      ].slice(-200), // keep last 200 messages
    })),

  resetRoom: () => {
    // Disconnect and drop the WS before resetting state
    useRoomStore.getState().ws?.disconnect();
    set(initialState);
  },
}));
